import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
import { getStorage } from 'src/hooks/use-local-storage';

/** Kept local to avoid importing the storage module for one string. */
const ACCESS_TOKEN_KEY = 'accessToken';

export const isRoleAdmin = (): boolean => {
  const { ADMIN, USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { role } = getStorage(USER_STORAGE_KEY);
    return role === ADMIN;
  }
  return false;
};

export const isRoleCustomer = (): boolean => {
  const { CUSTOMER, USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { role } = getStorage(USER_STORAGE_KEY);
    return role === CUSTOMER;
  }
  return false;
};

export const isLoggedIn = (): boolean => {
  const { USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { email } = getStorage(USER_STORAGE_KEY);

    return email || false;
  }
  return false;
};

export const isUserAgent = (): boolean => {
  const { AGENT, USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { role } = getStorage(USER_STORAGE_KEY);
    return role === AGENT;
  }
  return false;
};

/**
 * NAT-2331 — whether the signed-in user browses and orders as a client they
 * pick, rather than as themselves.
 *
 * Agents have always done this; admins now do too. Read this instead of
 * `isUserAgent()` anywhere the question is "is a client being acted for", so
 * the two roles cannot drift apart. `isUserAgent()` stays for the places that
 * genuinely mean "is an agent" — the agent dashboard, commissions, and the
 * agent's own associated-clients list.
 */
export const canActAsClient = (): boolean => isUserAgent() || isRoleAdmin();

/**
 * NAT-2530 — cost and margin (purchase cost, merchandise cost, gross margin, margin %) are for
 * admins only. Agents see the prices they sell at, their commission and revenue — never what the
 * goods cost. Read this, not `!isUserAgent()`, wherever a cost or margin is rendered or exported,
 * so any role that is not an admin is covered.
 *
 * This only hides; the backend strips the fields from every non-admin response, which is the
 * real control.
 *
 * The coffee dashboard shows margin to the coffee role by decision (NAT-2788), outside this helper.
 */
export const canSeeCostAndMargin = (): boolean => isRoleAdmin();

/** The table fields that carry a cost or a margin (order list, order lines). */
export const COST_FIELDS: readonly string[] = ['total_cost', 'margin', 'base_cost'];

/**
 * A table's columns as this user may see them: all of them for an admin, without the cost and
 * margin columns for anyone else. Use it on any column list that also drives an export, so the
 * download drops the same columns as the screen.
 */
export const costAwareColumns = <T extends { field: string }>(
  columns: T[],
  showCost: boolean = canSeeCostAndMargin(),
): T[] => (showCost ? columns : columns.filter((column) => !COST_FIELDS.includes(column.field)));

export const isUserAuditor = (): boolean => {
  const { AUDITOR, USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { role } = getStorage(USER_STORAGE_KEY);
    return role === AUDITOR;
  }
  return false;
};

export const isUserCoffee = (): boolean => {
  const { COFFEE, USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  if (getStorage(USER_STORAGE_KEY)) {
    const { role } = getStorage(USER_STORAGE_KEY);
    return role === COFFEE;
  }
  return false;
};

/** A coffee partner may only be sent back to /coffee itself (optionally with a query or hash); only that page exists. */
export const isCoffeeReturnTo = (returnTo: string | null | undefined): returnTo is string =>
  !!returnTo &&
  !returnTo.includes('\\') &&
  !returnTo.includes('..') &&
  /^\/coffee\/?(?:[?#].*)?$/.test(returnTo);

/** What analytics reports for anyone we cannot name. */
export const ANONYMOUS_ROLE = 'anonymous';

/**
 * The signed-in user's role, straight from storage, or `anonymous`.
 *
 * Exists so analytics can label the very first event of a page load correctly.
 * Init used to register `anonymous` unconditionally, overwriting the correct
 * role localStorage already held from the previous session, and only a React
 * effect put it back once auth state resolved — so every event in between was
 * mislabelled. All nine recorded sign-ins carried `role: 'customer'` on the
 * event and `user_role: 'anonymous'` as the super-property. NAT-2318.
 *
 * Deliberately defensive: this runs before React, on whatever happens to be in
 * storage, and a throw here would take the analytics module down with it. A
 * non-string role is refused rather than passed along — one of the two user
 * shapes in `src/types/user.ts` types `role` as a number, and a numeric role
 * reaching Mixpanel would be worse than no role at all.
 */
/**
 * Coerce whatever a user object calls a "role" into something safe to attach to
 * every analytics event.
 *
 * Every writer of the `user_role` super-property goes through here, so the rules
 * cannot disagree between the startup path and the post-auth path:
 *
 * - a non-string is refused. The login payload in localStorage is untyped
 *   (`AuthUserType = Record<string, any>`), and a numeric role reaching Mixpanel
 *   would be worse than none.
 * - an admin reports `anonymous`, as they always have. Labelling staff was tried
 *   and reverted: it stranded a stale role after sign-out, and it created a
 *   filterable staff cohort in a product that also records sessions. Its own
 *   ticket, with its own privacy decision.
 */
export const roleForAnalytics = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return ANONYMOUS_ROLE;
  return value === CONSTANT_VARIABLES.ADMIN ? ANONYMOUS_ROLE : value;
};

/**
 * The signed-in user's role, read straight from storage, or `anonymous`.
 *
 * Exists so analytics does not throw away a role it already has. Init used to
 * register `anonymous` unconditionally, overwriting the persisted role from the
 * previous session, and only a React effect put it back once auth resolved —
 * so every event in between was mislabelled. NAT-2318.
 */
export const storedUserRole = (): string => {
  const { USER_STORAGE_KEY } = CONSTANT_VARIABLES;

  try {
    /**
     * Gate on the ACCESS TOKEN, not on the stored user.
     *
     * `AuthProvider.initialize()` treats the token as the signed-in predicate:
     * no token means it dispatches a logged-out state, whatever the `user` key
     * still says. The two can diverge — a partial storage clear, a quota
     * eviction, a stale key from an older build — and reading `user` alone would
     * then label a signed-out visitor `customer` for the whole document, with
     * nothing to correct it (the analytics effect only calls `reset()` when it
     * has previously identified someone, which on that load it has not).
     *
     * The unconditional `anonymous` this replaced was accidentally covering that
     * case. Use the app's own predicate so the cover is deliberate.
     */
    if (!window?.localStorage?.getItem(ACCESS_TOKEN_KEY)) return ANONYMOUS_ROLE;

    return roleForAnalytics(getStorage(USER_STORAGE_KEY)?.role);
  } catch {
    // Belt and braces only: `getStorage` already swallows both a parse failure
    // and the property-access throw Safari raises with cookies disabled, so this
    // is currently unreachable. It is here so a future change to `getStorage`
    // cannot silently arm a throw on the startup path.
    return ANONYMOUS_ROLE;
  }
};
